<template>
  <div :class="$style.products" data-product-selector-v="3.1.0">
    <div ref="swiper" :class="['swiper', $style.swiper]">
      <div :class="'swiper-wrapper'">
        <div
          v-for="(product, index) in products"
          :key="product?.sys?.id"
          :class="['swiper-slide']"
        >
          <ProductSelectorItem
            v-if="product"
            :product="product"
            :is-active="selectedProduct?.sys?.id === product?.sys?.id"
            @click="handleSetActiveProduct(product?.sys?.id, index)"
          />
        </div>
      </div>
    </div>
    <div :class="$style.swiperControls">
      <button
        class="swiper-prev"
        :class="[
          $style.swiperPrev,
          !computedHasPrevSlide && $style.swiperControlDisabled,
        ]"
        :aria-label="localizedStrings['previous'] || 'Previous'"
        @click="handleSetActiveSlide(currentSlideIndex - 1)"
      >
        <IconChevronLeft :class="$style.chevronLeft" />
      </button>
      <button
        class="swiper-next"
        :class="[
          $style.swiperNext,
          !computedHasNextSlide && $style.swiperControlDisabled,
        ]"
        :aria-label="localizedStrings['next'] || 'Next'"
        @click="handleSetActiveSlide(currentSlideIndex + 1)"
      >
        <IconChevronRight :class="$style.chevronRight" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Swiper from 'swiper'

import type { TypeProductWithoutUnresolvableLinksResponse } from '~/types/contentful'

import 'swiper/css'

const { localizedStrings } = useLocalizedStrings()
const siteMeasurementStore = useSiteMeasurementStore()

const emit = defineEmits(['set-active-product'])

const MOBILE_SLIDES_PER_VIEW = 2
const TABLET_SLIDES_PER_VIEW = 3
const DESKTOP_SLIDES_PER_VIEW = 6

const props = defineProps<{
  products: (TypeProductWithoutUnresolvableLinksResponse | undefined)[]
  selectedProduct?: TypeProductWithoutUnresolvableLinksResponse
}>()

// Data
const swiperElRef = useTemplateRef<HTMLElement>('swiper')
const swiperInstance = ref<null | Swiper>(null)
const currentProductIndex = ref(0)
const currentSlideIndex = ref(0)

// Computed
const computedHasPrevSlide = computed(() => {
  const viewportWidth = siteMeasurementStore?.vWidth
  const slidesPerView =
    viewportWidth < 768
      ? MOBILE_SLIDES_PER_VIEW
      : viewportWidth < 1200
        ? TABLET_SLIDES_PER_VIEW
        : DESKTOP_SLIDES_PER_VIEW
  return currentSlideIndex.value > 0 && slidesPerView < props.products?.length
})
const computedHasNextSlide = computed(() => {
  const viewportWidth = siteMeasurementStore?.vWidth
  const slidesPerView =
    viewportWidth < 768
      ? MOBILE_SLIDES_PER_VIEW
      : viewportWidth < 1200
        ? TABLET_SLIDES_PER_VIEW
        : DESKTOP_SLIDES_PER_VIEW
  return currentSlideIndex.value + slidesPerView < props.products?.length
})

// Methods
const handleSetActiveSlide = (index: number) => {
  swiperInstance?.value?.slideTo?.(index)
  currentSlideIndex.value = index
}

const handleSetActiveProduct = (id: string, index: number) => {
  emit('set-active-product', id)
  currentProductIndex.value = index
  handleSetActiveSlide(index)
}

const initilizeSwiper = () => {
  if (swiperElRef.value) {
    swiperInstance.value = new Swiper(swiperElRef.value, {
      autoHeight: true,
      slidesPerView: MOBILE_SLIDES_PER_VIEW,
      slidesPerGroup: 1,
      spaceBetween: 13,
      watchSlidesProgress: true,
      allowTouchMove: false,
      navigation: {
        nextEl: '.swiper-next',
        prevEl: '.swiper-prev',
      },
      breakpoints: {
        768: {
          slidesPerView: TABLET_SLIDES_PER_VIEW,
        },
        1200: {
          slidesPerView: DESKTOP_SLIDES_PER_VIEW,
          spaceBetween: 18,
        },
      },
    })
  }
}

onMounted(() => {
  initilizeSwiper()
})
</script>

<style lang="scss" module>
.products {
  overflow: hidden;
  padding: 30px 20px 30px;
  position: relative;

  @include media('lg') {
    margin: 50px auto;
    overflow: visible;
    padding: 0;
    width: 1190px;
  }
}

.swiper {
  overflow: visible;
}

.swiperControls {
  align-content: flex-start;
  display: flex;
  height: 39px;
  justify-content: space-between;
  padding: 0 5px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-19px, -39px);
  width: 100%;
  z-index: 1;

  @include media('lg') {
    padding: 0;
    transform: translate(-19px, -39px);
    width: calc(100% + 39px);
  }
}

.swiperPrev,
.swiperNext {
  align-items: center;
  background-color: var(--purple);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 39px;
  justify-content: center;
  pointer-events: auto;
  width: 39px;
}

.swiperControlDisabled {
  opacity: 0;
  pointer-events: none;
}

.chevronLeft,
.chevronRight {
  color: var(--white);
  height: 13px;
  width: 8px;
}

:global {
  :local(.products) {
    .swiper-slide:not(.swiper-slide-visible) {
      opacity: 0.5;
    }
  }
}
</style>
